/*
 * Ankway landing page - interactive behaviour.
 * Stands in for the Replo runtime with the three components the page uses:
 * an infinite marquee, a looping testimonial carousel and FAQ collapsibles.
 */
(function () {
  'use strict';

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  /* ---------------------------------------------------------------- marquee
     The track holds its content twice and the CSS animation shifts it by
     --replo-marquee-width / --replo-marquee-repetitions, so the loop is
     seamless as soon as we report the measured track width. */
  function initMarquee(root) {
    root.querySelectorAll('[data-replo-marquee-root]').forEach(function (el) {
      var track = el.querySelector('[data-replo-marquee-track]');
      if (!track) return;

      var measure = function () {
        el.style.setProperty('--replo-marquee-width', track.scrollWidth + 'px');
      };

      measure();
      track.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', measure, { once: true });
      });
      window.addEventListener('resize', debounce(measure, 150));
    });
  }

  /* --------------------------------------------------------------- carousel
     Transform based, one slide per step, looping through cloned copies of the
     slide set placed either side of the originals. */
  function initCarousel(root) {
    root.querySelectorAll('[data-replo-carousel]').forEach(function (el) {
      var track = el.querySelector('[data-replo-part="slide-track"]');
      if (!track) return;

      var originals = Array.prototype.slice.call(track.children);
      var count = originals.length;
      if (count < 2) return;

      // A copy before and after the originals gives room to scroll either way.
      var addCopies = function (where) {
        originals.forEach(function (slide) {
          var copy = slide.cloneNode(true);
          copy.setAttribute('data-is-cloned', 'true');
          copy.removeAttribute('id');
          copy.removeAttribute('data-is-active');
          copy.querySelectorAll('[id]').forEach(function (n) {
            n.removeAttribute('id');
          });
          if (where === 'before') track.insertBefore(copy, track.firstChild);
          else track.appendChild(copy);
        });
      };
      addCopies('after');
      addCopies('before');

      var live = el.querySelector('.replo-sr-only');
      var index = 0; // position within the original set
      var animating = false;

      function step() {
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return originals[0].getBoundingClientRect().width + gap;
      }

      function offsetFor(i) {
        return -(count + i) * step();
      }

      function place(animate) {
        track.style.transitionProperty = animate ? 'transform' : 'none';
        track.style.transitionDuration = animate ? '400ms' : '0ms';
        track.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';
        track.style.transform = 'translate3d(' + offsetFor(index) + 'px, 0px, 0px)';

        Array.prototype.forEach.call(track.children, function (s) {
          s.removeAttribute('data-is-active');
        });
        var active = track.children[count + index];
        if (active) active.setAttribute('data-is-active', 'true');
        if (live) live.textContent = 'Slide ' + (index + 1) + ' of ' + count;
      }

      function go(delta) {
        if (animating) return;
        animating = true;
        index += delta;

        // Slide onto the clone first, then rebase into the original set.
        track.style.transitionProperty = 'transform';
        track.style.transitionDuration = '400ms';
        track.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';
        track.style.transform = 'translate3d(' + offsetFor(index) + 'px, 0px, 0px)';

        window.setTimeout(function () {
          if (index < 0) index += count;
          else if (index >= count) index -= count;
          place(false);
          animating = false;
        }, 400);
      }

      el.querySelectorAll('[data-replo-part="nav-button"]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          go(btn.getAttribute('data-direction') === 'previous' ? -1 : 1);
        });
      });

      // Pointer drag / touch swipe.
      var startX = null;
      var startAt = 0;
      track.addEventListener('pointerdown', function (e) {
        if (animating) return;
        startX = e.clientX;
        startAt = offsetFor(index);
        track.style.transitionDuration = '0ms';
      });
      window.addEventListener('pointermove', function (e) {
        if (startX === null) return;
        track.style.transform =
          'translate3d(' + (startAt + e.clientX - startX) + 'px, 0px, 0px)';
      });
      window.addEventListener('pointerup', function (e) {
        if (startX === null) return;
        var moved = e.clientX - startX;
        startX = null;
        if (Math.abs(moved) > step() / 4) go(moved < 0 ? 1 : -1);
        else place(true);
      });

      place(false);
      window.addEventListener('resize', debounce(function () { place(false); }, 150));
      track.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', function () { place(false); }, { once: true });
        }
      });
    });
  }

  /* ----------------------------------------------------------- collapsibles
     The trigger is the first child and points at its panel through
     aria-controls; the panel carries `hidden` while closed. */
  function initCollapsible(root) {
    root.querySelectorAll('[data-replo-collapsible]').forEach(function (el) {
      var trigger = el.querySelector('[aria-controls]');
      if (!trigger) return;

      var id = trigger.getAttribute('aria-controls');
      var panel = null;
      try {
        panel = el.querySelector(
          '#' + (window.CSS && CSS.escape ? CSS.escape(id) : id)
        );
      } catch (err) {
        panel = null;
      }
      if (!panel) panel = trigger.nextElementSibling;
      if (!panel) return;

      trigger.setAttribute(
        'aria-expanded',
        String(el.getAttribute('data-state') === 'open')
      );

      // `replo-animated` carries overflow:hidden, so it may only be present
      // while the slide runs - leaving it on would pin the panel's height.
      var timer = null;
      var measure = function () {
        panel.style.setProperty(
          '--radix-collapsible-content-height',
          panel.scrollHeight + 'px'
        );
        panel.style.setProperty(
          '--radix-collapsible-content-width',
          panel.scrollWidth + 'px'
        );
      };

      var toggle = function () {
        var open = el.getAttribute('data-state') !== 'open';
        el.setAttribute('data-state', open ? 'open' : 'closed');
        trigger.setAttribute('aria-expanded', String(open));
        window.clearTimeout(timer);

        if (open) panel.hidden = false;
        measure();
        panel.classList.add('replo-animated');
        panel.setAttribute('data-state', open ? 'open' : 'closed');

        timer = window.setTimeout(function () {
          panel.classList.remove('replo-animated');
          if (!open) panel.hidden = true;
        }, 200);
      };

      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    });
  }

  function init() {
    document.querySelectorAll('.ankway-landing').forEach(function (root) {
      if (root.dataset.ankwayReady) return;
      root.dataset.ankwayReady = '1';
      initMarquee(root);
      initCarousel(root);
      initCollapsible(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // The theme editor re-renders a section without a full page load.
  document.addEventListener('shopify:section:load', init);
})();
